/**
 * useSuggestedQuestions - background hook for the LiveMeetingPage middle column.
 *
 * Maintains a short list (max 6) of AI-generated follow-up questions that adapt
 * as the conversation evolves.  Completely separate from useDealCoaching - it does
 * not persist field answers and does not reference fieldKeys.
 *
 * Lifecycle:
 *   1. Once extraction finishes loading (isLoadingExtract = false) and sections are
 *      available, fire an initial AI seed call before any transcript arrives.
 *   2. Once ≥3 finalized transcript chunks exist, schedule an AI suggest call
 *      (debounced 2.5 s) to replace the active suggestions with conversation-aware ones.
 *   3. Each AI call passes field descriptions alongside labels so the model has richer
 *      context for generating targeted questions.
 *   4. The AI response carries `addressed` - per-item texts the model believes were
 *      raised in the transcript (Pass 1 output). Those items move to the asked list.
 *   5. markAsked(id) moves a single suggestion from active → asked (user action).
 *   6. dismissSuggestion(id) silently removes a suggestion without moving it to asked.
 *   7. A guard ref prevents concurrent AI calls. If new chunks arrive while a call is
 *      in-flight, a retry fires 500 ms after the call completes.
 *
 * Log tag: [LiveMeetingSuggest]
 */

import { isCompositeControl } from '@protoss/shared';
import * as React from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { salesApi } from '../../sales/services/salesApi';
import type { LiveMeetingStatus, SuggestedQuestion, TranscriptChunk } from '../types/live-meeting';
import type { ExtractSection, ExtractSectionField } from './useDealExtract';

// ── Stable ID generator ────────────────────────────────────────────────────
let _idSeq = 0;
function nextSuggestionId(): string {
  return `sq-${++_idSeq}`;
}

/**
 * Expands a field label to include nested option context for complex field
 * types (checklist, select_matrix). This gives the AI enough context to
 * generate natural sub-questions (e.g. "Which revenue models apply to your
 * business?") rather than only knowing the top-level label.
 */
function expandFieldLabel(field: ExtractSectionField): string {
  const itemOptions = field.options.filter((o) => !o.optionType || o.optionType === 'item');
  if (isCompositeControl(field.uiControl) && itemOptions.length > 0) {
    return `${field.label} (${itemOptions.map((o) => o.label).join(', ')})`;
  }
  return field.label;
}

// ── Hook ───────────────────────────────────────────────────────────────────

export interface UseSuggestedQuestionsReturn {
  activeSuggestions: SuggestedQuestion[];
  askedSuggestions: SuggestedQuestion[];
  /** Marks a suggestion as asked - moves it to the asked list. */
  markAsked: (id: string) => void;
  /** Silently removes a suggestion without moving it to the asked list. */
  dismissSuggestion: (id: string) => void;
  /** True while an AI suggest call is in-flight. */
  isInferring: boolean;
  /** Dev-only: clears all suggestions and immediately re-triggers the AI seed call. */
  reset: () => void;
}

/**
 * @param transcriptChunks    Finalized transcript chunks from useLiveMeetingSession.
 * @param sessionStatus       Current meeting status.
 * @param sections            Live extraction sections from useDealExtract - used to
 *                            derive outstanding question labels for the AI prompt.
 * @param isLoadingExtract    True while useDealExtract is still fetching - gates
 *                            the initial seed call so sections are complete first.
 */
export function useSuggestedQuestions({
  transcriptChunks,
  sessionStatus,
  sections,
  sessionUuid,
  isLoadingExtract = false,
}: {
  transcriptChunks: TranscriptChunk[];
  sessionStatus: LiveMeetingStatus;
  sections: ExtractSection[];
  sessionUuid?: string | null;
  isLoadingExtract?: boolean;
}): UseSuggestedQuestionsReturn {
  const { user } = useAuth();

  const [activeSuggestions, setActiveSuggestions] = React.useState<SuggestedQuestion[]>([]);
  const [askedSuggestions, setAskedSuggestions] = React.useState<SuggestedQuestion[]>([]);
  const [isInferring, setIsInferring] = React.useState(false);

  // Always-current refs to avoid stale closures inside setTimeout callbacks.
  const transcriptChunksRef = React.useRef(transcriptChunks);
  transcriptChunksRef.current = transcriptChunks;

  const sectionsRef = React.useRef(sections);
  sectionsRef.current = sections;

  const userRef = React.useRef(user);
  userRef.current = user;

  // Keeps the latest active list accessible inside runSuggestCall without
  // needing it as a closure dependency (would stale otherwise).
  const activeSuggestionsRef = React.useRef(activeSuggestions);
  activeSuggestionsRef.current = activeSuggestions;

  // Concurrency guard - prevents overlapping AI calls.
  const isSuggestingRef = React.useRef(false);
  const suggestTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastChunkCountRef = React.useRef(0);
  const sessionUuidRef = React.useRef(sessionUuid);
  sessionUuidRef.current = sessionUuid;
  // Prevents the initial AI seed call from firing more than once per session.
  const initialCallFiredRef = React.useRef(false);

  // Reset whenever a new session starts.
  React.useEffect(() => {
    if (sessionStatus !== 'active') return;
    setActiveSuggestions([]);
    setAskedSuggestions([]);
    lastChunkCountRef.current = 0;
    initialCallFiredRef.current = false;
  }, [sessionStatus]);

  // Reactively drop active suggestions whenever the right-rail extraction answers
  // a field - handles the race where the suggest call resolved before extraction
  // finished, so the initial filtering missed those fields.
  React.useEffect(() => {
    if (activeSuggestionsRef.current.length === 0) return;
    const answeredFieldKeys = new Set(
      sections
        .flatMap((s) => s.groups.flatMap((g) => g.fields))
        .filter((f) => f.state.status === 'answered')
        .map((f) => f.fieldKey)
    );
    if (answeredFieldKeys.size === 0) return;
    const nowAnswered = activeSuggestionsRef.current.filter(
      (s) => s.fieldKey != null && answeredFieldKeys.has(s.fieldKey)
    );
    if (nowAnswered.length === 0) return;
    const nowAnsweredIds = new Set(nowAnswered.map((s) => s.id));
    setAskedSuggestions((prev) => [
      ...prev,
      ...nowAnswered.filter((n) => !prev.some((a) => a.id === n.id)),
    ]);
    setActiveSuggestions((prev) => prev.filter((s) => !nowAnsweredIds.has(s.id)));
  }, [sections]);

  // Seed initial suggestions via AI using the first section's fields, called
  // once on session start before any transcript arrives.
  // Gated on isLoadingExtract to ensure sections are fully populated.
  React.useEffect(() => {
    if (sessionStatus !== 'active') return;
    if (isLoadingExtract) return;
    if (sections.length === 0) return;
    if (transcriptChunksRef.current.length >= 3) return;
    if (initialCallFiredRef.current) return;
    if (isSuggestingRef.current) return;

    initialCallFiredRef.current = true;
    isSuggestingRef.current = true;
    setIsInferring(true);

    const firstSection = sections.reduce((a, b) => (a.displayOrder <= b.displayOrder ? a : b));
    const pendingFields = firstSection.groups
      .flatMap((q) => q.fields)
      .filter((f) => f.state.status === 'pending');
    const sectionsPayload = [
      {
        label: firstSection.label,
        fieldKeys: pendingFields.map((f) => f.fieldKey),
        fieldLabels: pendingFields.map(expandFieldLabel),
        fieldDescriptions: pendingFields.map((f) => f.description ?? ''),
      },
    ];

    salesApi
      .suggestLiveMeetingQuestions({
        recentSegments: [],
        sections: sectionsPayload,
        hostName: userRef.current?.displayName ?? null,
        sessionUuid: sessionUuidRef.current ?? null,
      })
      .then(({ suggestions }) => {
        if (suggestions.length > 0) {
          const currentSections = sectionsRef.current;
          const answeredFieldKeys = new Set(
            currentSections
              .flatMap((s) => s.groups.flatMap((g) => g.fields))
              .filter((f) => f.state.status === 'answered')
              .map((f) => f.fieldKey)
          );
          const mapped = suggestions
            .map((s) => ({
              id: nextSuggestionId(),
              text: s.text,
              group: s.sectionLabel,
              fieldKey: s.fieldKey ?? undefined,
            }))
            .filter((s) => s.fieldKey == null || !answeredFieldKeys.has(s.fieldKey))
            .slice(0, 5);
          if (mapped.length > 0) setActiveSuggestions(mapped);
        }
      })
      .catch((err: unknown) => {
        console.error('[LiveMeetingSuggest] initial suggest error', err);
      })
      .finally(() => {
        isSuggestingRef.current = false;
        setIsInferring(false);
      });
  }, [sections, sessionStatus, isLoadingExtract]);

  // Core suggest runner - extracted so it can be retried from the finally block.
  const runSuggestCall = React.useCallback(() => {
    if (isSuggestingRef.current) return;
    isSuggestingRef.current = true;
    setIsInferring(true);

    const chunks = transcriptChunksRef.current;
    lastChunkCountRef.current = chunks.length;

    const currentSections = sectionsRef.current;
    const sectionsPayload = currentSections
      .map((s: ExtractSection) => {
        const unansweredFields = s.groups
          .flatMap((q) => q.fields)
          .filter((f: ExtractSectionField) => f.state.status !== 'answered');
        return {
          label: s.label,
          fieldKeys: unansweredFields.map((f: ExtractSectionField) => f.fieldKey),
          fieldLabels: unansweredFields.map(expandFieldLabel),
          fieldDescriptions: unansweredFields.map((f: ExtractSectionField) => f.description ?? ''),
        };
      })
      .filter((s) => s.fieldLabels.length > 0);

    const recentSegments = chunks.slice(-10).map((c: TranscriptChunk) => ({
      speakerName: c.userName,
      text: c.text,
    }));

    const currentSuggestions = activeSuggestionsRef.current.map((s) => s.text);

    salesApi
      .suggestLiveMeetingQuestions({
        recentSegments,
        sections: sectionsPayload,
        currentSuggestions,
        hostName: userRef.current?.displayName ?? null,
        sessionUuid: sessionUuidRef.current ?? null,
      })
      .then(({ suggestions, addressed }) => {
        const latestSections = sectionsRef.current;

        // Build a set of fieldKeys that have been answered in the right-rail extraction.
        const answeredFieldKeys = new Set(
          latestSections
            .flatMap((s) => s.groups.flatMap((g) => g.fields))
            .filter((f) => f.state.status === 'answered')
            .map((f) => f.fieldKey)
        );

        // Move items to asked if: AI text-matched them OR their tracked field is answered.
        const addressedTextSet = new Set(addressed);
        const addressedItems = activeSuggestionsRef.current.filter(
          (s) =>
            addressedTextSet.has(s.text) ||
            (s.fieldKey != null && answeredFieldKeys.has(s.fieldKey))
        );
        const addressedIds = new Set(addressedItems.map((s) => s.id));

        if (addressedItems.length > 0) {
          setAskedSuggestions((prevAsked) => [
            ...prevAsked,
            ...addressedItems.filter((n) => !prevAsked.some((a) => a.id === n.id)),
          ]);
        }

        // Always remove addressed items; keep remaining unasked questions.
        const MAX_ACTIVE = 5;
        setActiveSuggestions((prev) => {
          const remaining = prev.filter((s) => !addressedIds.has(s.id));
          const slotsAvailable = MAX_ACTIVE - remaining.length;
          if (suggestions.length === 0 || slotsAvailable <= 0) return remaining;
          // Avoid duplicating questions already in the remaining list or already answered.
          const existingTexts = new Set(remaining.map((s) => s.text));
          const toAdd = suggestions
            .filter((s) => !existingTexts.has(s.text))
            .map((s) => ({
              id: nextSuggestionId(),
              text: s.text,
              group: s.sectionLabel,
              fieldKey: s.fieldKey ?? undefined,
            }))
            .filter((s) => s.fieldKey == null || !answeredFieldKeys.has(s.fieldKey))
            .slice(0, slotsAvailable);
          return [...remaining, ...toAdd];
        });
      })
      .catch((err: unknown) => {
        console.error('[LiveMeetingSuggest] API error', err);
      })
      .finally(() => {
        isSuggestingRef.current = false;
        setIsInferring(false);
        // If new chunks arrived while we were running, schedule a retry.
        if (transcriptChunksRef.current.length > lastChunkCountRef.current) {
          if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
          suggestTimerRef.current = setTimeout(runSuggestCall, 500);
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced trigger whenever transcript grows (minimum 3 chunks before first call).
  React.useEffect(() => {
    if (sessionStatus !== 'active') return;
    const newCount = transcriptChunks.length;
    if (newCount < 3) return;
    if (newCount <= lastChunkCountRef.current) return;

    if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    suggestTimerRef.current = setTimeout(runSuggestCall, 2500);

    return () => {
      if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    };
  }, [transcriptChunks.length, sessionStatus, runSuggestCall]);

  // Clicking a suggestion moves only that individual item to the asked list.
  const markAsked = React.useCallback((id: string) => {
    setActiveSuggestions((prev) => {
      const item = prev.find((s) => s.id === id);
      if (!item) return prev;
      setAskedSuggestions((prevAsked) =>
        prevAsked.some((a) => a.id === id) ? prevAsked : [...prevAsked, item]
      );
      return prev.filter((s) => s.id !== id);
    });
  }, []);

  // Silently removes a suggestion without recording it as asked.
  const dismissSuggestion = React.useCallback((id: string) => {
    setActiveSuggestions((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const reset = React.useCallback(() => {
    setActiveSuggestions([]);
    setAskedSuggestions([]);
    lastChunkCountRef.current = 0;
    isSuggestingRef.current = false;
    if (suggestTimerRef.current) {
      clearTimeout(suggestTimerRef.current);
    }
    // Re-trigger suggest call immediately against current transcript.
    suggestTimerRef.current = setTimeout(runSuggestCall, 100);
  }, [runSuggestCall]);

  return { activeSuggestions, askedSuggestions, markAsked, dismissSuggestion, isInferring, reset };
}
